import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import {
  createLocalPatient,
  fetchLocalPatients,
  fetchLocalPatientById,
  updateLocalPatient,
  deactivateLocalPatient,
  reactivateLocalPatient,
  deleteLocalPatient,
} from "../thunks/localPatient.thunks";

const initialState = {
  byId: {},
  allIds: [],
  loading: false,
  loaded: false,
  error: null,
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    if (Array.isArray(value.data)) return value.data;
    if (Array.isArray(value.items)) return value.items;
    if (Array.isArray(value.results)) return value.results;
    if (Array.isArray(value.patients)) return value.patients;
  }
  return [];
};

const normalizeLocalPatient = (patient) => {
  if (!patient) return null;

  const id = String(patient.id ?? patient._id ?? patient.patientId ?? "").trim();
  if (!id) return null;

  const firstName = String(patient.firstName ?? "").trim();
  const lastName = String(patient.lastName ?? "").trim();
  const name =
    String(patient.name ?? "").trim() || `${firstName} ${lastName}`.trim();

  return {
    ...patient,
    id,
    identifier: String(patient.identifier ?? patient.patientIdentifier ?? "").trim(),
    name: name || "Local patient",
    fullName: name || "Local patient",
    firstName,
    lastName,
    dateOfBirth: patient.dateOfBirth || patient.birthdate || patient.birthDate || null,
    birthdate: patient.dateOfBirth || patient.birthdate || patient.birthDate || null,
    birthDate: patient.dateOfBirth || patient.birthdate || patient.birthDate || null,
    phone: String(patient.phone ?? "").trim(),
    email: patient.email ?? null,
    age: patient.age ?? null,
    source: "local",
    isActive: Boolean(patient.isActive),
  };
};

const upsertOne = (state, patient) => {
  const normalized = normalizeLocalPatient(patient);
  if (!normalized) return;

  state.byId[normalized.id] = normalized;
  if (!state.allIds.includes(normalized.id)) {
    state.allIds.push(normalized.id);
  }
};

const upsertMany = (state, patients) => {
  asArray(patients).forEach((patient) => upsertOne(state, patient));
};

const localPatientSlice = createSlice({
  name: "localPatients",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLocalPatients.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.error = null;
        state.byId = {};
        state.allIds = [];
        upsertMany(state, action.payload);
      })
      .addCase(fetchLocalPatientById.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        upsertOne(state, action.payload);
      })
      .addCase(updateLocalPatient.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        upsertOne(state, action.payload);
      })
      .addCase(deactivateLocalPatient.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        upsertOne(state, action.payload);
      })
      .addCase(reactivateLocalPatient.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        upsertOne(state, action.payload);
      })
      .addCase(deleteLocalPatient.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const id = String(action.payload?.patientId ?? "");
        delete state.byId[id];
        state.allIds = state.allIds.filter((patientId) => patientId !== id);
      })
      .addCase(createLocalPatient.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        upsertOne(state, action.payload);
      })
      .addMatcher(
        isAnyOf(
          fetchLocalPatients.pending,
          fetchLocalPatientById.pending,
          createLocalPatient.pending,
          updateLocalPatient.pending,
          deactivateLocalPatient.pending,
          reactivateLocalPatient.pending,
          deleteLocalPatient.pending,
        ),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        isAnyOf(
          fetchLocalPatients.rejected,
          fetchLocalPatientById.rejected,
          createLocalPatient.rejected,
          updateLocalPatient.rejected,
          deactivateLocalPatient.rejected,
          reactivateLocalPatient.rejected,
          deleteLocalPatient.rejected,
        ),
        (state, action) => {
          state.loading = false;
          state.loaded = true;
          const payload = action.payload;
          state.error =
            typeof payload === "string"
              ? payload
              : payload?.message || "Failed to manage local patients";
        }
      );
  },
});

export default localPatientSlice.reducer;
